/**
 * Clamp a raw HTMLInputElement number string to non-negative.
 * - Empty string passes through (so the input can show "0" placeholder).
 * - Any finite negative value collapses to '0'.
 * - Anything else (positive, NaN-ish, etc.) passes through unchanged so
 *   downstream Decimal.js parsing or browser sanitization can handle it.
 *
 * Mirrors the inline helper that lived in Wizard.tsx through v1.0.2.
 */
export function clampNonNegative(raw: string): string {
  if (raw === '') return raw;
  const n = parseFloat(raw);
  if (Number.isFinite(n) && n < 0) return '0';
  return raw;
}

/**
 * v1.0.4 P0 (defect C): floor a *cents* string at zero. Used in the
 * reducer and on hydration from IDB to ensure no negative amount_cents
 * record can reach the engine. The engine itself also floors, so this is
 * defense-in-depth — never the only safeguard.
 *
 * Accepts the same signed-integer-cents format as engine/decimal.ts
 * (e.g. '-500000', '111100', '0'). Empty string maps to '0' (legacy
 * defensive case; production callers pass real numbers).
 */
export function floorAmountCents(centsString: string): string {
  if (centsString === '' || centsString === undefined || centsString === null) {
    return '0';
  }
  // Reject anything that isn't a signed integer string. Coercing here
  // could mask data corruption — let the caller see the bad input by
  // returning '0' (matches the "floor" contract).
  if (!/^-?\d+$/.test(centsString)) return '0';
  return centsString.startsWith('-') && centsString !== '-0' ? '0' : centsString;
}
